import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageIcon, Loader2, X } from 'lucide-react';
import { useGalleryListQuery } from '@/features/gallery/api/useGalleryQueries';
import {
  firstGalleryMedia,
  galleryItemId,
  isGalleryVideo,
  type GalleryItem,
} from '@/features/gallery/types/gallery';

type GalleryPickerModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (item: GalleryItem) => void;
};

export function GalleryPickerModal({
  open,
  onClose,
  onSelect,
}: GalleryPickerModalProps) {
  const [page, setPage] = useState(1);
  const listQuery = useGalleryListQuery({
    page,
    limit: 12,
    status: 'APPROVE',
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  const items = listQuery.data?.data ?? [];
  const totalPages = listQuery.data?.totalPages || 1;
  const loading = listQuery.isLoading || listQuery.isFetching;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      <button
        type="button"
        aria-label="Close overlay"
        className="absolute inset-0 border-0 bg-black/45 outline-none"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal
        aria-label="Select from gallery"
        className="relative z-10 flex max-h-[min(80vh,640px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border-0 bg-white shadow-2xl outline-none"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4">
          <div className="min-w-0 text-left">
            <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)] sm:text-lg">
              My Gallery
            </h2>
            <p className="mt-0.5 mb-0 truncate text-xs text-[var(--dash-muted)] sm:text-sm">
              Select an approved creative
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border-0 bg-transparent p-2 text-[var(--dash-muted)] outline-none transition hover:bg-[var(--dash-soft)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 sm:px-4 sm:pb-4">
          {loading && items.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-[var(--dash-brand)]" />
              <p className="m-0 text-sm text-[var(--dash-muted)]">
                Loading gallery…
              </p>
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--dash-soft)]">
                <ImageIcon className="h-7 w-7 text-[var(--dash-muted)] opacity-50" />
              </div>
              <h3 className="m-0 text-sm font-semibold text-[var(--dash-ink)]">
                No approved content found
              </h3>
              <p className="m-0 max-w-xs text-xs text-[var(--dash-muted)]">
                Approved creatives from Gallery will appear here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
              {items.map((item) => {
                const media = firstGalleryMedia(item);
                const url = media?.url;
                const video = isGalleryVideo(media);
                const id = galleryItemId(item);
                return (
                  <button
                    type="button"
                    key={id || item.contentName}
                    onClick={() => {
                      onSelect(item);
                      onClose();
                    }}
                    className="group relative aspect-square overflow-hidden rounded-xl border-0 bg-[var(--dash-soft)] text-left outline-none transition hover:ring-2 hover:ring-[var(--dash-brand)]/40"
                  >
                    {!url ? (
                      <div className="flex h-full items-center justify-center text-[var(--dash-muted)]">
                        <ImageIcon className="h-7 w-7 opacity-40" />
                      </div>
                    ) : video ? (
                      <video
                        src={url}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-full w-full border-0 object-cover"
                      />
                    ) : (
                      <img
                        src={url}
                        alt={item.contentName || 'Gallery creative'}
                        className="h-full w-full border-0 object-cover"
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100">
                      <span className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-[var(--dash-brand)] shadow">
                        Select
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-2 py-1.5 text-[10px] font-medium text-white opacity-0 transition group-hover:opacity-100">
                      {item.contentName || 'Untitled'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {totalPages > 1 ? (
          <div className="flex shrink-0 items-center justify-between gap-3 bg-[var(--dash-soft)] px-4 py-2.5">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border-0 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--dash-ink)] outline-none disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-xs font-medium text-[var(--dash-muted)]">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border-0 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--dash-ink)] outline-none disabled:opacity-50"
            >
              Next
            </button>
          </div>
        ) : (
          <div className="shrink-0 bg-[var(--dash-soft)] px-4 py-2.5 text-center">
            <p className="m-0 text-xs text-[var(--dash-muted)]">
              Showing approved content from your gallery
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
